import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Mail, Phone, UserPlus, Search, FileText, ListChecks, Receipt, Pencil, Trash2 } from "lucide-react";
import { useData } from "@/context/DataContext";
import ClientFormDialog from "@/components/dialogs/ClientFormDialog";
import ConfirmDialog from "@/components/dialogs/ConfirmDialog";
import { toast } from "sonner";

function initials(name) {
  return name
    .split(" ")
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

export default function Clients() {
  const { clients, addClient, updateClient, deleteClient } = useData();
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setAddOpen(true);
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const filtered = clients.filter(
    (c) =>
      c.name.toLowerCase().includes(query.toLowerCase()) ||
      (c.company || "").toLowerCase().includes(query.toLowerCase())
  );

  const handleAdd = (form) => {
    addClient(form);
    toast.success(`${form.name} added`);
  };
  const handleEdit = (form) => {
    updateClient(selected.id, form);
    setSelected({ ...selected, ...form });
    toast.success(`${form.name} updated`);
  };
  const handleDelete = () => {
    const name = selected?.name;
    deleteClient(selected.id);
    setSelected(null);
    setConfirmOpen(false);
    toast.success(`${name} deleted`);
  };

  return (
    <div className="space-y-8" data-testid="clients-page">
      <PageHeader
        title="Client Management"
        subtitle="Track your clients, their status, and their progress — all in one place."
        actions={
          <Button
            data-testid="add-client-button"
            onClick={() => setAddOpen(true)}
            className="bg-primary hover:bg-primary/90 rounded-lg btn-premium"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Add Client
          </Button>
        }
      />

      <Card className="border border-slate-200 dark:border-slate-800 rounded-xl elev-1 overflow-hidden">
        <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="h-4 w-4 text-slate-400 dark:text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              data-testid="clients-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search clients..."
              className="pl-9 h-9 bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800 rounded-lg"
            />
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400">
            {filtered.length} of {clients.length}
          </div>
        </div>

        <Table data-testid="clients-table">
          <TableHeader>
            <TableRow className="bg-slate-50 dark:bg-slate-900/60 dark:bg-slate-900/60 hover:bg-slate-50 dark:hover:bg-slate-800/60 dark:bg-slate-900/60 dark:bg-slate-900/60 border-b border-slate-200 dark:border-slate-800">
              <TableHead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400 font-semibold">Name</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400 font-semibold hidden md:table-cell">Company</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400 font-semibold hidden lg:table-cell">Email</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400 font-semibold">Status</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400 font-semibold hidden md:table-cell">Added</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((c) => (
              <TableRow
                key={c.id}
                data-testid={`client-row-${c.id}`}
                onClick={() => setSelected(c)}
                className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/60 dark:bg-slate-900/80 dark:bg-slate-900/40 border-b border-slate-100 dark:border-slate-800"
              >
                <TableCell>
                  <div className="flex items-center gap-3">
                    <Avatar className="h-9 w-9 border border-slate-200 dark:border-slate-800">
                      <AvatarFallback className="bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-xs font-medium">
                        {initials(c.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="text-sm font-medium text-slate-900 dark:text-slate-100">{c.name}</div>
                      <div className="text-xs text-slate-500 dark:text-slate-400 md:hidden">{c.company}</div>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="hidden md:table-cell text-sm text-slate-600 dark:text-slate-300">
                  {c.company}
                </TableCell>
                <TableCell className="hidden lg:table-cell text-sm text-slate-600 dark:text-slate-300">
                  {c.email}
                </TableCell>
                <TableCell>
                  <StatusBadge status={c.status} testid={`client-status-${c.id}`} />
                </TableCell>
                <TableCell className="hidden md:table-cell text-sm text-slate-500 dark:text-slate-400">
                  {c.added}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="client-profile-sheet">
          {selected && (
            <>
              <SheetHeader className="space-y-4">
                <div className="flex items-center gap-4">
                  <Avatar className="h-14 w-14 border border-slate-200 dark:border-slate-800">
                    <AvatarFallback className="bg-primary text-primary-foreground text-sm font-semibold">
                      {initials(selected.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <SheetTitle className="font-display text-xl">{selected.name}</SheetTitle>
                    <SheetDescription>{selected.company}</SheetDescription>
                    <div className="mt-2">
                      <StatusBadge status={selected.status} />
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid="edit-client-button"
                    onClick={() => setEditOpen(true)}
                    className="rounded-lg"
                  >
                    <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid="delete-client-button"
                    onClick={() => setConfirmOpen(true)}
                    className="rounded-lg text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/40 dark:bg-red-950/50 border-slate-200 dark:border-slate-800"
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete
                  </Button>
                </div>
              </SheetHeader>

              <div className="mt-8 space-y-6">
                <section>
                  <div className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-[0.2em] mb-3">
                    Contact info
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-3 text-sm text-slate-700 dark:text-slate-300">
                      <Mail className="h-4 w-4 text-slate-400 dark:text-slate-500" />
                      {selected.email}
                    </div>
                    <div className="flex items-center gap-3 text-sm text-slate-700 dark:text-slate-300">
                      <Phone className="h-4 w-4 text-slate-400 dark:text-slate-500" />
                      {selected.phone}
                    </div>
                  </div>
                </section>

                <section>
                  <div className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-[0.2em] mb-3">
                    Notes
                  </div>
                  <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-3">
                    {selected.notes || "No notes yet."}
                  </p>
                </section>

                <section>
                  <div className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-[0.2em] mb-3 flex items-center gap-2">
                    <ListChecks className="h-3.5 w-3.5" />
                    Tasks
                  </div>
                  {(selected.tasks || []).length ? (
                    <ul className="space-y-1.5">
                      {(selected.tasks || []).map((t, i) => (
                        <li key={i} className="text-sm text-slate-700 dark:text-slate-300 pl-3 border-l-2 border-slate-200 dark:border-slate-800">
                          {t}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-slate-500 dark:text-slate-400">No active tasks.</p>
                  )}
                </section>

                <section>
                  <div className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-[0.2em] mb-3 flex items-center gap-2">
                    <Receipt className="h-3.5 w-3.5" />
                    Orders
                  </div>
                  {(selected.orders || []).length ? (
                    <ul className="space-y-1.5">
                      {(selected.orders || []).map((o, i) => (
                        <li key={i} className="text-sm text-slate-700 dark:text-slate-300 pl-3 border-l-2 border-slate-200 dark:border-slate-800">
                          {o}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-slate-500 dark:text-slate-400">No orders yet.</p>
                  )}
                </section>

                <section>
                  <div className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-[0.2em] mb-3 flex items-center gap-2">
                    <FileText className="h-3.5 w-3.5" />
                    Documents
                  </div>
                  {(selected.documents || []).length ? (
                    <ul className="space-y-1.5">
                      {(selected.documents || []).map((d, i) => (
                        <li key={i} className="text-sm text-slate-700 dark:text-slate-300 pl-3 border-l-2 border-slate-200 dark:border-slate-800">
                          {d}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-slate-500 dark:text-slate-400">No documents.</p>
                  )}
                </section>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <ClientFormDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onSubmit={handleAdd}
        title="Add Client"
      />
      <ClientFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        onSubmit={handleEdit}
        initial={selected}
        title="Edit Client"
      />
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={`Delete ${selected?.name}?`}
        description="This client will be removed from the demo session."
        onConfirm={handleDelete}
        testid="delete-client-confirm"
      />
    </div>
  );
}
